import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { RiskNotice } from "@/components/RiskNotice";
import { listCasinos } from "@/data/casinos";
import { FAQ, buildFAQSchema } from "@/components/FAQ";

type Variant = "default" | "bankid" | "konto" | "registrering";

export function UnlicensedCasinoPage({ variant = "default" }: { variant?: Variant }) {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const casinos = listCasinos({ noSwedishLicense: true }).sort(
    (a, b) =>
      Number(b.priority_brand) - Number(a.priority_brand) ||
      b.overall_rating - a.overall_rating,
  );

  const titlesSv: Record<Variant, string> = {
    default: "Casino utan svensk licens — komplett guide 2026",
    bankid: "Casino utan BankID 2026",
    konto: "Casino utan konto 2026",
    registrering: "Casino utan registrering 2026",
  };
  const titlesEn: Record<Variant, string> = {
    default: "Casinos without a Swedish license — full guide 2026",
    bankid: "Casinos without BankID 2026",
    konto: "Casinos without account 2026",
    registrering: "Casinos without registration 2026",
  };

  const title = (isSv ? titlesSv : titlesEn)[variant];
  const description = isSv
    ? "Casinon utan svensk licens kan ha större bonusar och fler spel — men erbjuder lägre konsumentskydd. Förstå riskerna innan du spelar."
    : "Casinos without a Swedish license can offer bigger bonuses and a wider catalog — but lower consumer protection. Understand the risks before playing.";

  const faq = [
    {
      question_sv: "Är det olagligt att spela på casino utan svensk licens?",
      question_en: "Is it illegal to play at casinos without a Swedish license?",
      answer_sv:
        "Nej, det är inte olagligt för en svensk privatperson att spela på utländska casinon, men casinot får inte rikta marknadsföring mot Sverige. Konsumentskyddet skiljer sig från svensk reglering.",
      answer_en:
        "No, it is not illegal for a Swedish private individual to play at foreign casinos, but the casino is not allowed to market to Sweden. Consumer protection differs from Swedish regulation.",
    },
    {
      question_sv: "Måste jag betala skatt på vinster?",
      question_en: "Do I need to pay tax on winnings?",
      answer_sv:
        "Vinster från casinon utanför EES kan vara skattepliktiga i Sverige. Spel inom EES brukar vara skattefritt — men reglerna kan ändras. Kontrollera alltid med Skatteverket eller en oberoende rådgivare.",
      answer_en:
        "Winnings from casinos outside the EEA can be taxable in Sweden. Gambling inside the EEA is typically tax-free — but rules change. Always verify with Skatteverket or an independent advisor.",
    },
    {
      question_sv: "Är jag ansluten till Spelpaus?",
      question_en: "Am I covered by Spelpaus?",
      answer_sv:
        "Nej. Spelpaus.se gäller endast svenskt licensierade bolag. Om du har spelproblem och behöver fullt skydd är casino utan svensk licens fel väg — kontakta Stödlinjen 020-81 91 00.",
      answer_en:
        "No. Spelpaus.se only applies to Swedish-licensed operators. If you struggle with gambling and need full protection, casinos without a Swedish license are the wrong route — contact Stödlinjen at 020-81 91 00.",
    },
    {
      question_sv: "Vad ska jag kontrollera innan jag registrerar mig?",
      question_en: "What should I check before registering?",
      answer_sv:
        "Operatörens licensnummer (MGA, Curaçao, Estland eller annan), ägarbolagets säte, bonusvillkor i sin helhet, max insats per spin, KYC-krav vid uttag, samt om svensk valuta och svensk support finns.",
      answer_en:
        "The operator's license number (MGA, Curaçao, Estonia or other), the parent company's domicile, full bonus terms, max bet per spin, KYC requirements at withdrawal, and whether Swedish currency and Swedish-language support are available.",
    },
  ];

  return (
    <div className="container-page py-8">
      <SEO
        title={title}
        description={`${title}. ${description}`}
        jsonLd={buildFAQSchema(faq, lang)}
      />
      <Breadcrumbs
        items={[{ label: t("nav.home"), href: prefix }, { label: title }]}
      />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{description}</p>

      <div className="mt-4">
        <RiskNotice variant="unlicensed" />
      </div>

      <section className="mt-8 prose prose-ink max-w-none">
        <h2>{isSv ? "Vad är skillnaden mot svenskt licensierade casinon?" : "What is the difference vs Swedish-licensed casinos?"}</h2>
        <p>
          {isSv
            ? "Casinon utan svensk licens drivs under utländska tillstånd — vanligen MGA (Malta), Curaçao, Estland eller andra. Reglerna kring bonus, max insats, KYC och konsumentskydd kan skilja sig markant från det du är van vid på svenska sajter. Storleken på välkomstbonusarna är ofta större men i utbyte gäller andra och ibland tuffare omsättningskrav."
            : "Casinos without a Swedish license operate under foreign permits — typically MGA (Malta), Curaçao, Estonia or others. The rules around bonuses, max bet, KYC and consumer protection can differ markedly from Swedish sites. Welcome bonuses are usually larger, but the trade-off is different and sometimes tougher wagering requirements."}
        </p>
        <h3>{isSv ? "Risker att förstå" : "Risks to understand"}</h3>
        <ul>
          <li>{isSv ? "Inget skydd via Spelpaus.se" : "No protection via Spelpaus.se"}</li>
          <li>{isSv ? "Möjlig skatteplikt på vinster utanför EES" : "Possible tax liability on winnings outside the EEA"}</li>
          <li>{isSv ? "Tvister hanteras av licensjurisdiktionen — inte av ARN" : "Disputes handled by the license jurisdiction — not Sweden's ARN"}</li>
          <li>{isSv ? "Inga obligatoriska insättningsgränser" : "No mandatory deposit limits"}</li>
          <li>{isSv ? "Stöd och språk inte garanterat på svenska" : "Support and language not guaranteed in Swedish"}</li>
        </ul>
        <h3>{isSv ? "När det kan vara rätt val" : "When it might be the right choice"}</h3>
        <p>
          {isSv
            ? "Du har full kontroll över ditt spelande, vill ha tillgång till slots eller bonusar som inte finns under svensk reglering, och accepterar att dispatcherna inte är desamma som hos en svensk licensierad sajt. Det är aldrig rätt val om du har spelproblem eller är avstängd via Spelpaus."
            : "You have full control over your gambling, want access to slots or bonuses unavailable under Swedish regulation, and accept the dispute resolution is not Swedish. It is never the right choice if you struggle with gambling or are excluded via Spelpaus."}
        </p>
      </section>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Casinon utan svensk licens — vår lista" : "Casinos without a Swedish license — our list"}
      </h2>
      <div className="mt-4 grid gap-4">
        {casinos.map((c, i) => (
          <CasinoCard key={c.id} casino={c} rank={i + 1} source="unlicensed-list" />
        ))}
      </div>

      <div className="mt-10">
        <FAQ items={faq} />
      </div>

      <InternalLinks context="unlicensed" />
    </div>
  );
}
